import React from 'react';
import {Box, Typography} from '@mui/material';
import {ReactComponent as CaretDownIcon} from '../../../assets/images/caret-base-icon.svg';
import {ReactComponent as TickMarkIcon} from '../../../assets/images/tick-mark-base-icon.svg';
import {ReactComponent as CrossIcon} from '../../../assets/images/close-base-icon.svg';
import './ArrSelectionChip.scss';

export type ArrSelectionChipData = {
  id: string;
  label: string;
  type?: string;
  isSelected: boolean;
};

type ArrSelectionChipProps = {
  filterData: ArrSelectionChipData;
  onClick: (event: any, param: ArrSelectionChipData) => void;
  isFilterInactive?: boolean;
  variant?: 'v1' | 'v2';
  showCloseIcon?: boolean;
  noIcon?: boolean;
  labelTextTestId?: string;
  dataTestId?: string;
  tabIndex?: number;
};

const ArrSelectionChip: React.FC<ArrSelectionChipProps> = ({
  filterData,
  onClick,
  isFilterInactive = false,
  variant = 'v1',
  showCloseIcon = false,
  noIcon = false,
  labelTextTestId,
  dataTestId,
  tabIndex,
}) => {
  const handleClick = (e: React.MouseEvent) => {
    if (!isFilterInactive && !showCloseIcon) {
      onClick(e, filterData);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isFilterInactive && !showCloseIcon) {
      if (e.key === 'Enter' || e.code === 'Space' || e.key === ' ') {
        e.stopPropagation();
        e.preventDefault();
        onClick(e, filterData);
      }
    }
  };

  const handleCloseClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isFilterInactive && showCloseIcon) {
      onClick(e, filterData);
    }
  };
  const clickable = !isFilterInactive && !showCloseIcon;
  return (
    <Box
      aria-label={filterData.label}
      data-testid={`arr-selection-chip-${dataTestId ?? filterData.id}`}
      className={`arrSelectionChip ${variant}-selection-chip ${
        isFilterInactive
          ? 'inactive-state'
          : filterData.isSelected
            ? 'selected-state'
            : 'non-selected-state'
      }`}
      tabIndex={!isFilterInactive && !showCloseIcon ? 0 : -1}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      sx={{
        pointerEvents: isFilterInactive ? 'none' : 'auto',
        cursor: clickable ? 'pointer' : 'default',
      }}
    >
      <Box className="items-alignment">
        <Typography
          className={`typo-text label3_size ${
            isFilterInactive
              ? 'inactive-label'
              : filterData.isSelected
                ? 'selected-label'
                : 'filter-label'
          } ${noIcon ? 'without-icon-label' : ''}`}
          aria-label={filterData.label}
          data-testid={`chip-valuess-${labelTextTestId ?? dataTestId ?? filterData.id}`}
          tabIndex={tabIndex ?? 0}
        >
          {filterData.label}
        </Typography>

        {!noIcon &&
          !showCloseIcon &&
          (isFilterInactive ? (
            <CaretDownIcon className="inactive-caret-down-icon" />
          ) : filterData.isSelected ? (
            <TickMarkIcon className="tick-mark-icon" />
          ) : (
            <CaretDownIcon className="caret-down-icon" />
          ))}

        {!noIcon && showCloseIcon && (
          <CrossIcon
            className={`close-icon ${isFilterInactive ? 'inactive-close-icon' : ''}`}
            style={{cursor: isFilterInactive ? 'default' : 'pointer'}}
            onClick={handleCloseClick}
            aria-label="close-icon"
            role="button"
            tabIndex={0}
            data-testid={`arr-selection-chip-close-icon-${filterData.id}`}
          />
        )}
      </Box>
    </Box>
  );
};

export default ArrSelectionChip;
